import {
    Button,
    Card,
    CardActions,
    CardContent,
    FormControlLabel,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import { EntityRadioField } from 'app/components/form-controls/entity-radio-field';
import { StorePicker } from 'app/components/store-picker';
import { Translate } from 'app/components/translate';
import { StoreDeletionEnum } from 'app/enum';
import { Store } from 'app/model/cris/store';
import { messagingService } from 'app/services';
import { dialogService } from 'app/services/dialog.service';
import { storeService } from 'app/services/store.service';
import { enumKeys } from 'app/utils';

interface StoreDeletionProps {
    store: Store;
}

const defaultValues: Record<string, boolean> = {};

enumKeys(StoreDeletionEnum).forEach((key) => {
    const value = StoreDeletionEnum[key];
    defaultValues[value] = true;
});

export const StoreDeletion: React.FC<StoreDeletionProps> = ({ store }) => {
    const [selection, setSelection] = useState<Record<string, boolean>>(defaultValues);
    const [targetStore, setTargetStore] = useState<Store>();
    const history = useHistory();

    const handleDeletion = useCallback(
        (deleteSelections: Record<string, boolean>) => {
            storeService
                .delete(store, undefined, {
                    store_delete_selection: {
                        targetStore: targetStore?.id,
                        destiny: deleteSelections,
                    },
                })
                .subscribe(() => {
                    messagingService.showSuccess('Store Successfully Deleted');
                    history.replace(`../${targetStore?.id ?? ''}`);
                });
        },
        [history, store, targetStore?.id],
    );

    return (
        <>
            <Typography variant='h3'>Store Deletion - {store.name}</Typography>
            <Typography variant='body1' gutterBottom>
                In order to delete the store you must choose what should happen with all related records (ex. patterns).
                This will delete the preprod store and add the production counterpart to a deletion queue with same
                selection as were made here.
            </Typography>

            <Card variant='outlined' className='mb-2'>
                <CardContent>
                    <Typography variant='h5'>1. Select Destination Store</Typography>
                    <Typography variant='body1'>This is the store all related items will be moved to.</Typography>
                    <StorePicker
                        label='Destination Store'
                        size='small'
                        getOptionDisabled={(option) => store.id === option.id}
                        value={targetStore}
                        onChange={(evt, target) => setTargetStore(target)}
                    />
                </CardContent>
            </Card>
            <Card variant='outlined' className='mb-2'>
                <CardContent>
                    <Typography variant='h5'>2. Select Destiny of Items</Typography>
                    <Typography variant='body1'>
                        Please, select what happens to each of items linked to this store.
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Destiny</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {enumKeys(StoreDeletionEnum).map((enumKey) => (
                                    <TableRow key={enumKey}>
                                        <TableCell>
                                            <Translate tkey={`forms:StoreDeletionEnum.${enumKey}`} />
                                        </TableCell>
                                        <TableCell>
                                            <EntityRadioField
                                                name={StoreDeletionEnum[enumKey]}
                                                label=''
                                                value={selection}
                                                row
                                                onChange={(evt, name, value) => {
                                                    setSelection({ ...selection, [name]: 'true' === value });
                                                }}
                                            >
                                                <FormControlLabel value='true' control={<Radio />} label='Keep' />
                                                <FormControlLabel
                                                    disabled={
                                                        StoreDeletionEnum[enumKey] ===
                                                            StoreDeletionEnum.PARTNER_MAP_QUERIES ||
                                                        StoreDeletionEnum[enumKey] ===
                                                            StoreDeletionEnum.PARTNER_MAP_QUERY_MODS
                                                    }
                                                    value='false'
                                                    control={<Radio />}
                                                    label='Discard'
                                                />
                                            </EntityRadioField>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Card variant='outlined' className='mb-2'>
                <CardContent>
                    <Typography variant='h5'>3. Execute Deletion</Typography>
                    <Typography variant='body1'>There is no going back once you click this.</Typography>
                </CardContent>
                <CardActions>
                    <Button
                        type='button'
                        variant='contained'
                        color='error'
                        onClick={() => {
                            dialogService.showConfirm(
                                {
                                    message: `Proceed with deletion of store "${store.name}"?`,
                                },
                                () => handleDeletion(selection),
                            );
                        }}
                    >
                        Delete
                    </Button>
                </CardActions>
            </Card>
        </>
    );
};
